import { base32, safeEqual } from './encoding';
import { assertId, deriveMemberSecret, findKey, hmac, type KeySet, type MemberCredential } from './keys';

export const DEFAULT_WINDOW_SECONDS = 30;
const MAC_BYTES = 10;

/** Window config comes from settings; a zero or infinite value must not reach the verify loop. */
export function assertWindowConfig(windowSeconds: number, skewWindows = 0): void {
  if (!Number.isFinite(windowSeconds) || windowSeconds <= 0) throw new RangeError(`invalid windowSeconds: ${windowSeconds}`);
  if (!Number.isInteger(skewWindows) || skewWindows < 0) throw new RangeError(`invalid skewWindows: ${skewWindows}`);
}

export function windowIndex(nowMs: number, windowSeconds = DEFAULT_WINDOW_SECONDS): number {
  assertWindowConfig(windowSeconds);
  return Math.floor(nowMs / (windowSeconds * 1000));
}

async function mac(secret: string, memberId: string, win: number): Promise<string> {
  return base32(await hmac(secret, `M1|${memberId}|${win}`, MAC_BYTES));
}

/** Mode 1, member app: `M1.<kid>.<memberId>.<mac>` — the window is implied by the clock (TOTP style). */
export async function generateMemberCode(cred: MemberCredential, nowMs: number, windowSeconds = DEFAULT_WINDOW_SECONDS): Promise<string> {
  assertId('memberId', cred.memberId);
  if (nowMs > cred.notAfter) throw new Error('credential expired; refresh from server');
  return `M1.${cred.kid}.${cred.memberId}.${await mac(cred.secret, cred.memberId, windowIndex(nowMs, windowSeconds))}`;
}

export type MemberVerifyResult =
  | { ok: true; memberId: string; kid: string; windowIndex: number }
  | { ok: false; reason: 'malformed' | 'unknown_key' | 'bad_signature' };

export interface VerifyOptions {
  windowSeconds?: number;
  /** Accept codes from ±skewWindows windows. Default 1. */
  skewWindows?: number;
}

/** Offline verification with the kiosk key set. Does NOT check replay — see ReplayGuard. */
export async function verifyMemberCode(code: string, keys: KeySet, nowMs: number, opts: VerifyOptions = {}): Promise<MemberVerifyResult> {
  const windowSeconds = opts.windowSeconds ?? DEFAULT_WINDOW_SECONDS;
  const skew = opts.skewWindows ?? 1;
  assertWindowConfig(windowSeconds, skew);
  if (typeof code !== 'string') return { ok: false, reason: 'malformed' };
  const parts = code.split('.');
  if (parts.length !== 4 || parts[0] !== 'M1') return { ok: false, reason: 'malformed' };
  const [, kid, memberId, given] = parts as [string, string, string, string];
  if (!kid || !memberId || !given) return { ok: false, reason: 'malformed' };
  const key = findKey(keys, kid, nowMs);
  if (!key) return { ok: false, reason: 'unknown_key' };
  let secret: string;
  try {
    secret = await deriveMemberSecret(key, memberId);
  } catch {
    return { ok: false, reason: 'malformed' };
  }
  const now = windowIndex(nowMs, windowSeconds);
  for (let w = now - skew; w <= now + skew; w++) {
    if (safeEqual(await mac(secret, memberId, w), given)) return { ok: true, memberId, kid, windowIndex: w };
  }
  return { ok: false, reason: 'bad_signature' };
}

/** In-memory replay guard: the same code is accepted once per kiosk while it could still verify. */
export class ReplayGuard {
  private seen = new Map<string, number>(); // code -> expiry ms
  /** Pass the same windowSeconds and skewWindows as verifyMemberCode, or codes outlive their entries. */
  constructor(private windowSeconds = DEFAULT_WINDOW_SECONDS, private skewWindows = 1) {
    assertWindowConfig(windowSeconds, skewWindows);
  }

  /** Returns true if first sight (accept), false if replay. */
  checkAndRecord(code: string, nowMs: number): boolean {
    this.prune(nowMs);
    if (this.seen.has(code)) return false;
    this.seen.set(code, nowMs + (2 * this.skewWindows + 2) * this.windowSeconds * 1000);
    return true;
  }

  get size(): number {
    return this.seen.size;
  }

  private prune(nowMs: number): void {
    for (const [c, exp] of this.seen) if (exp <= nowMs) this.seen.delete(c);
  }
}
